/* snippets/sw-cart-discount.liquid */

const SW_CDC_MESSAGE_KEY = 'swCartDiscountMessage';
const SW_CDC_CODES_KEY = 'swCartDiscountCodes';

class SwCartDiscount extends HTMLElement {
  connectedCallback() {
    if (this.bound) return;
    this.bound = true;

    this.input = this.querySelector('.sw-cdc__input');
    this.applyButton = this.querySelector('.sw-cdc__apply');
    this.applyLabel = this.querySelector('.sw-cdc__apply-label');
    this.messageEl = this.querySelector('.sw-cdc__message');
    this.spinner = this.querySelector('.sw-cdc__spinner');

    this.applyButton?.addEventListener('click', () => this.applyTyped());
    this.input?.addEventListener('input', () => this.setMessage(''));
    this.input?.addEventListener('keydown', (event) => {
      // The field lives inside the drawer's <form id="cart">, so Enter would
      // otherwise submit straight to checkout.
      if (event.key !== 'Enter') return;
      event.preventDefault();
      this.applyTyped();
    });

    this.querySelectorAll('.sw-cdc__remove').forEach((button) => {
      button.addEventListener('click', () => this.remove(button.dataset.code));
    });

    this.replayMessage();
  }

  /* The `discount` param replaces the whole set rather than adding to it, so
     every apply has to resend the codes already on the cart. Liquid can only
     offer discount_application.title, which is the discount's NAME and often
     is not the code, so codes this session applied are remembered separately
     and take precedence. */
  get appliedCodes() {
    const titles = (this.dataset.codes || '').split(',');
    const merged = [];

    [...readCodes(), ...titles].forEach((entry) => {
      const code = (entry || '').trim();
      if (!code) return;
      if (merged.some((kept) => kept.toLowerCase() === code.toLowerCase())) return;
      merged.push(code);
    });

    return merged;
  }

  without(code) {
    const dropped = (code || '').toLowerCase();
    return this.appliedCodes.filter((applied) => applied.toLowerCase() !== dropped);
  }

  applyTyped() {
    const code = (this.input?.value || '').trim();

    if (!code) {
      this.input?.focus();
      return;
    }

    this.submit([...this.without(code), code], code);
  }

  remove(code) {
    writeCodes(readCodes().filter((kept) => kept.toLowerCase() !== (code || '').toLowerCase()));
    this.submit(this.without(code), null, true);
  }

  submit(codes, expected, removed) {
    if (this.busy) return;
    this.setBusy(true);
    this.setMessage('');

    const miniCart = document.querySelector('mini-cart');
    const sections = miniCart ? miniCart.getSectionsToRender().map((section) => section.id) : [];
    const url = window.theme?.routes?.cart_update_url || '/cart/update';
    const config =
      typeof fetchConfig === 'function'
        ? fetchConfig()
        : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };

    fetch(url, {
      ...config,
      body: JSON.stringify({
        // An empty string clears every code — the only supported removal.
        discount: codes.join(','),
        sections,
        sections_url: window.location.pathname,
      }),
    })
      .then((response) => response.text().then((body) => ({ ok: response.ok, cart: parseJson(body) })))
      .then(({ ok, cart }) => {
        if (!ok || !cart) throw new Error();

        if (expected && !this.wasApplied(cart, expected)) {
          this.setMessage(this.dataset.error, 'error');
          this.input?.focus();
          return;
        }

        if (expected) {
          writeCodes([...readCodes().filter((kept) => kept.toLowerCase() !== expected.toLowerCase()), expected]);
        }

        this.stashMessage('');
        this.rerender(cart, miniCart, removed);
      })
      .catch(() => {
        this.setMessage(this.dataset.error, 'error');
        this.input?.focus();
      })
      .finally(() => this.setBusy(false));
  }

  wasApplied(cart, code) {
    const wanted = code.toLowerCase();

    // Shopify's own per-code verdict. The only signal that covers a
    // shipping-only code, which moves no cart-side price and so appears in no
    // discount application at all.
    const reported = (cart.discount_codes || []).find(
      (entry) => (entry?.code || '').toLowerCase() === wanted
    );
    if (reported) return reported.applicable !== false;

    // discount_application.title is the discount's NAME, which frequently is
    // not the code — so a title match can confirm, but never deny.
    const titles = (cart.cart_level_discount_applications || [])
      .filter((discount) => discount.type === 'discount_code')
      .map((discount) => discount.title);

    (cart.items || []).forEach((item) => {
      (item.line_level_discount_allocations || []).forEach((allocation) => {
        const application = allocation.discount_application;
        if (application?.type === 'discount_code') titles.push(application.title);
      });
    });

    if (titles.some((title) => (title || '').toLowerCase() === wanted)) return true;

    // Last resort: did the cart actually get cheaper? Resending the codes
    // already on the cart cannot move this on its own, so a rise means the
    // new code was accepted under a title we could not match.
    return Number(cart.total_discount || 0) > Number(this.dataset.totalDiscount || 0);
  }

  rerender(cart, miniCart, removed) {
    if (!miniCart || !cart.sections) {
      window.location.reload();
      return;
    }

    try {
      miniCart.renderContents({ sections: cart.sections });
    } catch (error) {
      console.error(error);
      window.location.reload();
      return;
    }

    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));

    // renderContents replaced this element, so focus has to be handed to the
    // freshly rendered one.
    const fresh = document.querySelector('sw-cart-discount');
    if (fresh && fresh !== this && !removed) fresh.querySelector('.sw-cdc__input')?.focus();
  }

  stashMessage(text) {
    if (!text) return;
    try {
      window.sessionStorage.setItem(SW_CDC_MESSAGE_KEY, text);
    } catch (error) {
      /* private mode */
    }
  }

  replayMessage() {
    let text = null;
    try {
      text = window.sessionStorage.getItem(SW_CDC_MESSAGE_KEY);
      window.sessionStorage.removeItem(SW_CDC_MESSAGE_KEY);
    } catch (error) {
      return;
    }
    if (text) this.setMessage(text);
  }

  setBusy(busy) {
    this.busy = busy;
    this.toggleAttribute('data-busy', busy);
    if (this.applyButton) this.applyButton.disabled = busy;
    if (this.applyLabel) this.applyLabel.hidden = busy;
    if (this.input) this.input.disabled = busy;
    if (this.spinner) this.spinner.hidden = !busy;
  }

  setMessage(text, state) {
    if (!this.messageEl) return;
    this.messageEl.textContent = text || '';
    this.messageEl.classList.toggle('sw-cdc__message--error', state === 'error');
  }
}

function parseJson(body) {
  try {
    return JSON.parse(body);
  } catch (error) {
    return null;
  }
}

function readCodes() {
  try {
    const stored = JSON.parse(window.sessionStorage.getItem(SW_CDC_CODES_KEY) || '[]');
    return Array.isArray(stored) ? stored.filter((code) => typeof code === 'string') : [];
  } catch (error) {
    return [];
  }
}

function writeCodes(codes) {
  try {
    window.sessionStorage.setItem(SW_CDC_CODES_KEY, JSON.stringify(codes));
  } catch (error) {
    /* private mode */
  }
}

customElements.define('sw-cart-discount', SwCartDiscount);
